import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router';
import CNLoading from 'src/components/loading-cn/CNLoading';
import TableRox from 'src/components/table-rox/TableRox';
import { paths } from 'src/routes/paths';
import { useMyDataStore } from 'src/store/me/me.store';
import { collectibleType } from 'src/types/collectibles';

type Props = {
  isLoadingCollectibles: boolean;
  collectiblesList: collectibleType[];
  collectionId?: string;
  updatingCollectibleTypeId: string | null;
  onToggleCollectibleType: (
    collectibleId: string,
    currentType: collectibleType['type']
  ) => Promise<void>;
};

type ToggleCollectibleTypeCellProps = {
  collectible: collectibleType;
  disabled: boolean;
  onToggle: Props['onToggleCollectibleType'];
};

function ToggleCollectibleTypeCell({
  collectible,
  disabled,
  onToggle,
}: ToggleCollectibleTypeCellProps) {
  const currentType = collectible.type === 'blocked' ? 'blocked' : 'free';
  const isBlocked = currentType === 'blocked';
  const actionLabel = isBlocked ? 'Tornar livre' : 'Bloquear colecionável';

  return (
    <Tooltip title={actionLabel}>
      <span>
        <IconButton
          aria-label={actionLabel}
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            onToggle(collectible._id, currentType);
          }}
          sx={{
            cursor: disabled ? 'default' : 'pointer',
            width: 42,
            height: 42,
            border: '1px solid',
            borderColor: isBlocked ? 'error.light' : 'success.light',
            bgcolor: isBlocked ? 'error.lighter' : 'success.lighter',
            color: isBlocked ? 'error.dark' : 'success.dark',
            transition: 'color 180ms ease, background-color 180ms ease, transform 180ms ease',
            '&:hover': {
              bgcolor: isBlocked ? 'error.light' : 'success.light',
              transform: disabled ? undefined : 'scale(1.06)',
            },
          }}
        >
          <Box
            key={currentType}
            component="span"
            sx={{
              display: 'inline-flex',
              '@keyframes collectibleLockSwap': {
                '0%': { opacity: 0, transform: 'scale(0.55) rotate(-18deg)' },
                '65%': { opacity: 1, transform: 'scale(1.12) rotate(4deg)' },
                '100%': { opacity: 1, transform: 'scale(1) rotate(0deg)' },
              },
              animation: 'collectibleLockSwap 320ms ease-out',
            }}
          >
            {isBlocked ? (
              <LockOutlinedIcon sx={{ fontSize: 26 }} />
            ) : (
              <LockOpenOutlinedIcon sx={{ fontSize: 26 }} />
            )}
          </Box>
        </IconButton>
      </span>
    </Tooltip>
  );
}

function CollectibleTypeCell({ value }: { value?: collectibleType['type'] }) {
  const isBlocked = value === 'blocked';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1.6,
        py: 0.6,
        borderRadius: 999,
        border: '1px solid',
        borderColor: isBlocked ? 'error.light' : 'success.light',
        backgroundColor: isBlocked ? 'error.lighter' : 'success.lighter',
        color: isBlocked ? 'error.dark' : 'success.dark',
        fontWeight: 600,
        fontSize: '0.875rem',
        minWidth: 138,
        justifyContent: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      {isBlocked ? (
        <LockOutlinedIcon sx={{ fontSize: 22, flexShrink: 0 }} />
      ) : (
        <LockOpenOutlinedIcon sx={{ fontSize: 22, flexShrink: 0 }} />
      )}
      <Box component="span">{isBlocked ? 'Bloqueado' : 'Livre'}</Box>
    </Box>
  );
}

export default function CollectiblesListByCollection({
  isLoadingCollectibles,
  collectiblesList,
  collectionId,
  updatingCollectibleTypeId,
  onToggleCollectibleType,
}: Props) {
  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);

  const HEADER = [
    {
      id: '_typeToggle',
      label: '',
      renderCell: (_: unknown, collectible: collectibleType) => (
        <ToggleCollectibleTypeCell
          collectible={collectible}
          disabled={!!updatingCollectibleTypeId || !permissions?.update}
          onToggle={onToggleCollectibleType}
        />
      ),
    },
    {
      id: 'name',
      label: 'Nome do Colecionável',
      avatar: true,
    },
    {
      id: 'simplifiedName',
      label: 'Nome Simplificado',
    },
    {
      id: 'price',
      label: 'Valor em Pontos',
    },
    {
      id: 'type',
      label: 'Tipo',
      renderCell: (value: collectibleType['type']) => <CollectibleTypeCell value={value} />,
    },
    {
      id: 'totalQuantity',
      label: 'Quantidade Total',
    },
    {
      id: 'availableQuantity',
      label: 'Quantidade Disponível',
    },
    {
      id: 'status',
      type: 'label',
      label: 'Status',
    },
  ];

  const navigate = useNavigate();

  return (
    <Stack spacing={1}>
      {isLoadingCollectibles ? (
        <Box flexGrow={1} display="flex" justifyContent="center" alignItems="center" mt={30}>
          <CNLoading />
        </Box>
      ) : (
        <TableRox
          data={collectiblesList || []}
          header={HEADER}
          defaultOrderBy="name"
          selectKey="_id"
          onClickKey="_id"
          avatarType="default"
          avatarKey="icon"
          tableTitle="Itens da Coleção"
          loading={!!updatingCollectibleTypeId}
          onClickFunction={(id) =>
            navigate(`${paths.dashboard.collectibleDetails(id)}?collectionId=${collectionId}`)
          }
          titleActions={
            <Button
              variant="contained"
              onClick={() =>
                navigate(`${paths.dashboard.collectibleCreate}?collectionId=${collectionId}`)
              }
              disabled={!permissions?.create}
            >
              Novo Colecionável
            </Button>
          }
        />
      )}
    </Stack>
  );
}
